import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectDoesUserHaveRbacPermission } from 'core/userContext';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import { NODE_TYPES } from 'modules/workflows/constants';
import { PromptType } from 'modules/workflows/components/JourneyAssist/JourneyAssist.constants';
import {
  getIsJourneyAIGenerationEnabled,
  getEdges,
  getNodes,
  getNodeMap,
  getSelectedNodes,
  getSelectedAINodes,
  getStartNode,
  getWorkflowType,
} from 'modules/workflows/ducks';
import {
  createEdgesMap,
  getTilesetBoundaries,
  getConnectedTiles,
  getNeighborEdges,
} from 'modules/workflows/utils/journeyAssistUtils';

/**
 * A hook to determine if the Journey Assist dialog should be shown
 * and whether the user is able to add or replace tiles.
 * Intended to share logic between studio footer and canvas components
 * @returns
 * - shouldShowJourneyAssist: boolean
 * - promptType: PromptType
 *
 */
export const useJourneyAssist = () => {
  const isJourneyAIGenerationEnabled = useSelector(getIsJourneyAIGenerationEnabled);
  const { isDraft } = useSelector(getWorkflowType);
  const canDraftMessaging = useSelector(selectDoesUserHaveRbacPermission)(
    PROJECT_PERMISSIONS.DraftMessaging,
  );
  const canUseJourneyAssist = isDraft && canDraftMessaging && isJourneyAIGenerationEnabled;

  const nodeMap = useSelector(getNodeMap);
  const nodes = useSelector(getNodes);
  const edges = useSelector(getEdges);
  const selected = useSelector(getSelectedNodes);
  const selectedAINodes = useSelector(getSelectedAINodes);
  const startNode = useSelector(getStartNode);

  const selectedIds = useMemo(() => selected.map(({ id }) => id.toString()), [selected]);

  const edgeMaps = useMemo(() => createEdgesMap(edges), [edges]);
  const { sourcesToTargetsMap } = edgeMaps;

  const isNoteTileSelected = useMemo(
    () => selected.some((node) => node.type === NODE_TYPES.NOTE),
    [selected],
  );

  const isConnectedStartTileSelected = useMemo(
    () => startNode?.isSelected && sourcesToTargetsMap[startNode?.id] && !selectedAINodes.length,
    [startNode, sourcesToTargetsMap, selectedAINodes],
  );

  const isEveryTileSelected = useMemo(
    () =>
      nodes.length > 0 &&
      nodes
        .filter((node) => node.type !== NODE_TYPES.NOTE)
        .every((node) => !!node.isSelected || !!node.isAIGenerated),
    [nodes],
  );

  const areAllTilesConnected = useMemo(
    () =>
      selectedIds.length > 0 && canUseJourneyAssist
        ? getConnectedTiles({ selectedIds, nodeMap, edgeMaps })
        : true,
    [canUseJourneyAssist, selectedIds, nodeMap, edgeMaps],
  );

  const shouldShowJourneyAssist = useMemo(() => {
    if (!canUseJourneyAssist) return false;
    if (isEveryTileSelected) return true;
    // We cannot add to/replace notes or replace start tiles so lets just hide the prompt
    if (isNoteTileSelected || isConnectedStartTileSelected) return false;

    return areAllTilesConnected;
  }, [
    canUseJourneyAssist,
    isConnectedStartTileSelected,
    isNoteTileSelected,
    isEveryTileSelected,
    areAllTilesConnected,
  ]);

  const promptType = useMemo(() => {
    if (!shouldShowJourneyAssist) return;
    if (nodes.length === 1 || isEveryTileSelected) return PromptType.NewJourney;

    return PromptType.NewTileset;
  }, [shouldShowJourneyAssist, nodes.length, isEveryTileSelected]);

  const addToPathTileId = useMemo(() => {
    if (promptType !== PromptType.NewTileset) return '';

    const { outputIds } = getTilesetBoundaries(selectedIds, edgeMaps);
    return outputIds.length === 1 ? outputIds[0] : '';
  }, [edgeMaps, selectedIds, promptType]);

  const { sourceEdges, targetEdges } = useMemo(
    () =>
      promptType === PromptType.NewTileset
        ? getNeighborEdges(selectedIds, nodeMap, edgeMaps)
        : { targetEdges: [], sourceEdges: [] },
    [edgeMaps, selectedIds, promptType, nodeMap],
  );

  return { shouldShowJourneyAssist, promptType, addToPathTileId, sourceEdges, targetEdges };
};
